import { PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react'
import { LayoutContext } from "./LayoutContext.tsx"
import { useGetUser, useUpdateUserLayoutMutation } from "../api/user/user.ts"
import Grid from "react-grid-layout"
import { Panels } from "../utils/constants.ts"

const initialLayout = {
    name: 'Default',
    config: [
        { i: Panels.MONEY_FLOW, x: 0, y: 0, w: 7, h: 20 },
        { i: Panels.DETAILS, x: 8, y: 0, w: 5, h: 20, minW: 5 },
        { i: Panels.TRANSACTIONS, x: 0, y: 1, w: 12, h: 15 },
    ] as Array<Grid.Layout>
}

export const COLS = 12
export const ROW_HEIGHT = 20
export const MARGIN = 10
const DEFAULT_HEIGHT = 300

export const LayoutProvider = ({ children }: PropsWithChildren) => {
    const { data: userInfo, isFetching } = useGetUser()
    const { mutate: updateUserLayout, mutateAsync, isPending } = useUpdateUserLayoutMutation()
    const [fullSize, setFullSize] = useState(false)
    const gridRef = useRef<HTMLDivElement>(null)
    const initial = userInfo?.layoutConfig?.layouts
        .find((l) => l.name === userInfo.layoutConfig.activeLayout) || initialLayout
    const [layout, setLayout] = useState<{ name: string, config: Array<Grid.Layout> }>(initial)
    const { layouts, activeLayout } = userInfo?.layoutConfig || { activeLayout: 'Default', layouts: [] }
    const rect = gridRef.current?.getBoundingClientRect()
    const visibleHeight = rect ? Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0) : DEFAULT_HEIGHT

    const updateLayout = useCallback(async (name: string, layout: Array<Grid.Layout>) => {
        const newLayouts = layouts.map(
            (l) => (l.name === name ? { name, config: layout } : l)
        )
        updateUserLayout({ activeLayout, layouts: newLayouts })
    }, [layouts, updateUserLayout, activeLayout])

    const addLayout = useCallback(async (name: string, layout: Array<Grid.Layout>) => {
        await mutateAsync({ activeLayout: name, layouts: layouts.concat({ name, config: layout }) })
    }, [layouts, mutateAsync])

    const removeLayout = useCallback((layoutName: string) => {
        setLayout(initialLayout)
        updateUserLayout({ activeLayout: 'Default', layouts: layouts.filter((l) => l.name !== layoutName) })
    }, [layouts, updateUserLayout])

    const setActiveLayout = useCallback((layoutName: string) => {
        setFullSize(false)
        setLayout(layouts.find((l) => l.name === layoutName) || initialLayout)
        updateUserLayout({ activeLayout: layoutName, layouts })
    }, [layouts, updateUserLayout])

    const resetLayout = useCallback(() => {
        setFullSize(false)
        setLayout(layouts.find((l) => l.name === activeLayout) || initialLayout)
    }, [layouts, activeLayout])

    const togglePanel = useCallback((panelName: Panels) => {
        if (!layouts) return
        setFullSize(false)
        if (layout.config.find((l) => l.i === panelName)) {
            setLayout((layout) =>
                ({ name: layout.name, config: layout.config.filter((l) => l.i !== panelName) }))
        } else {
            const newPanel = { i: panelName, x: 0, y: 0, w: 5, h: 15 }
            setLayout((layout) => ({
                name: layout.name,
                config: layout.config.concat(newPanel)
            }))
        }
    }, [layout, layouts])

    const allLayouts = useMemo(() => {
        if (!layouts) return [initialLayout]
        return layouts.concat(initialLayout)
    }, [layouts])

    const toggleFullSize = useCallback((panelKey: string) => {
        if (fullSize) {
            setLayout(layouts.find((l) => l.name === activeLayout) || initialLayout)
        } else {
            const filtered = structuredClone(layout.config.filter((p) => p.i === panelKey))
            filtered[0].w = COLS
            filtered[0].h = visibleHeight / (ROW_HEIGHT + MARGIN)
            filtered[0].x = 0
            filtered[0].y = 0
            setLayout({ name: layout.name, config: filtered })
        }
        setFullSize((prev) => !prev)
    }, [activeLayout, fullSize, layout.config, layout.name, layouts, visibleHeight])

    const value = useMemo(() => ({
        layout,
        layoutConfig: { layouts: allLayouts, activeLayout },
        _setLayout: setLayout,
        setActiveLayout,
        updateLayout,
        addLayout,
        removeLayout,
        resetLayout,
        togglePanel,
        fullSize,
        toggleFullSize,
        isFetching,
        isPending,
        gridRef
    }), [layout, allLayouts, activeLayout, setActiveLayout, updateLayout,
        addLayout, removeLayout, resetLayout, togglePanel, fullSize, toggleFullSize,
        isFetching, isPending])

    return (
        <LayoutContext.Provider value={value}>
            {children}
        </LayoutContext.Provider>
    )
}
